import React, { memo, useCallback, useMemo, useState } from 'react';
import { View, Image, StyleSheet, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import type { CorkBoardThreadSummary } from '@/types/residents/corkBoard';
import { ThreadActionsMenu } from '@/components/residents/corkBoard/ThreadActionsMenu';
import { GlassCard } from '../GlassCard';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { formatRelativeDate } from '@/utils/common/formatting';
import { getAvatarColor } from '@/utils/residents/avatarColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { SurveyCardCTA } from '@/components/residents/corkBoard/survey/SurveyCardCTA';

function getTagBadgeColor(tagColor: string | null) {
  const color = tagColor || PRIMITIVE_COLORS.resioBlue;
  const cleaned = color.replace('#', '');
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return { bg: undefined as string | undefined, text: PRIMITIVE_COLORS.resioBlue };
  }
  return { bg: `rgba(${r},${g},${b},0.1)`, text: color };
}

interface NewsfeedThreadCardProps {
  thread: CorkBoardThreadSummary;
  onPress: (thread: CorkBoardThreadSummary) => void;
  onTagPress?: (tagId: number) => void;
  onSave?: (thread: CorkBoardThreadSummary) => void;
  onEdit?: (thread: CorkBoardThreadSummary) => void;
  onDelete?: (thread: CorkBoardThreadSummary) => void;
  onReport?: (thread: CorkBoardThreadSummary) => void;
}

export const NewsfeedThreadCard: React.FC<NewsfeedThreadCardProps> = memo(({ thread, onPress, onTagPress, onSave, onEdit, onDelete, onReport }) => {
  const { colors, mode } = useThemeColors();
  const isAnnouncement = thread.isAnnouncement || thread.isPinned;
  const [menuVisible, setMenuVisible] = useState(false);
  const handlePress = useCallback(() => onPress(thread), [onPress, thread]);
  const handleMore = useCallback((e?: any) => {
    e?.stopPropagation?.();
    setMenuVisible(true);
  }, []);
  const closeMenu = useCallback(() => setMenuVisible(false), []);
  const avatarColor = useMemo(
    () => getAvatarColor(thread.creator.name, mode),
    [thread.creator.name, mode],
  );
  const firstTag = thread.tags[0];
  const tagBadgeColor = useMemo(
    () => firstTag ? getTagBadgeColor(firstTag.color) : null,
    [firstTag],
  );

  const fallbackBadgeBg = colors.newsfeed.announcementBadgeBg;
  // Patch undefined bg from getTagBadgeColor (NaN hex case)
  if (tagBadgeColor && tagBadgeColor.bg === undefined) {
    tagBadgeColor.bg = fallbackBadgeBg;
  }

  const hasComments = thread.replyCount > 0;

  return (
    <GlassCard
      interactive
      onPress={handlePress}
      borderRadius={16}
      padding={18}
      style={[
        styles.cardShadow,
        isAnnouncement ? { backgroundColor: colors.newsfeed.announcementBg, borderColor: colors.newsfeed.announcementBorder } : undefined,
      ]}
    >
      {isAnnouncement && (
        <View style={[styles.announcementBadge, { backgroundColor: colors.newsfeed.announcementBadgeBg }]}>
          <RESIDENTS_SVG_COMPONENTS.STAR width={12} height={12} color={PRIMITIVE_COLORS.resioBlue} />
          <Text style={styles.announcementText}>Announcement</Text>
        </View>
      )}

      <View style={styles.authorRow}>
        {thread.creator.photoUrl ? (
          <Image source={{ uri: thread.creator.photoUrl }} style={styles.avatarImage} />
        ) : (
          <View style={[styles.avatar, { backgroundColor: avatarColor.bg }]}>
            <Text style={[styles.avatarText, { color: avatarColor.text }]}>
              {thread.creator.initials}
            </Text>
          </View>
        )}

        <View style={styles.authorInfo}>
          <Text style={[styles.authorName, { color: colors.text.tertiary }]} numberOfLines={1}>{thread.creator.name}</Text>
          <Text style={[styles.timestamp, { color: colors.text.dimmer }]}>
            {isAnnouncement && thread.isPinned ? 'Pinned · ' : ''}
            {formatRelativeDate(thread.lastActivityAt)}
          </Text>
        </View>

        <View style={styles.menuAnchor}>
          <Hoverable
            hover="row"
            style={styles.moreButton}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            onPress={handleMore}
          >
            <RESIDENTS_SVG_COMPONENTS.THREE_DOTS width={16} height={16} color={colors.text.dimmer} />
          </Hoverable>
          <ThreadActionsMenu
            visible={menuVisible}
            onClose={closeMenu}
            isOwnThread={thread.isOwnThread}
            onSave={() => { closeMenu(); onSave?.(thread); }}
            onEdit={() => { closeMenu(); onEdit?.(thread); }}
            onDelete={() => { closeMenu(); setTimeout(() => onDelete?.(thread), 300); }}
            onReport={() => { closeMenu(); setTimeout(() => onReport?.(thread), 300); }}
            isSaved={thread.isSaved}
          />
        </View>
      </View>

      <Text style={[styles.title, { color: colors.text.tertiary }]} numberOfLines={2}>{thread.title}</Text>

      {thread.bodyPreview ? (
        <View style={styles.bodyContainer}>
          <Text scale="content" style={[styles.body, { color: colors.text.dim }]} numberOfLines={2}>{thread.bodyPreview}</Text>
        </View>
      ) : null}

      {thread.previewImageUrl ? (
        <Image
          source={{ uri: thread.previewImageUrl }}
          style={styles.previewImage}
          resizeMode="cover"
        />
      ) : null}

      {firstTag && tagBadgeColor && (
        <Hoverable
          hover="button"
          style={[styles.tagBadge, { backgroundColor: tagBadgeColor.bg }]}
          onPress={onTagPress ? () => onTagPress(Number(firstTag.id)) : undefined}
          disabled={!onTagPress}
        >
          <Text style={[styles.tagBadgeText, { color: tagBadgeColor.text }]}>
            {firstTag.name}
          </Text>
        </Hoverable>
      )}

      <View style={[styles.footer, { borderTopColor: colors.separator }]}>
        <View style={styles.statsRow}>
          <View style={styles.stat}>
            <RESIDENTS_SVG_COMPONENTS.CONVERSATION
              width={14}
              height={14}
              color={colors.text.dimmer}
            />
            <Text style={[styles.statText, { color: colors.text.dimmer }, hasComments && styles.statTextActive]}>
              {thread.replyCount}
            </Text>
          </View>

          <View style={styles.stat}>
            <RESIDENTS_SVG_COMPONENTS.NOTIFICATION
              width={14}
              height={14}
              color={colors.text.dimmer}
            />
            <Text style={[styles.statText, { color: colors.text.dimmer }]}>{thread.viewCount}</Text>
          </View>

          {thread.attachmentCount > 0 && (
            <View style={[styles.stat, styles.attachmentStat]}>
              <RESIDENTS_SVG_COMPONENTS.PAPERCLIP
                width={14}
                height={14}
                color={colors.text.dimmer}
              />
              <Text style={[styles.statText, { color: colors.text.dimmer }]}>{thread.attachmentCount}</Text>
            </View>
          )}
        </View>
      </View>

      {thread.isLocked && (
        <View style={styles.lockIndicator}>
          <RESIDENTS_SVG_COMPONENTS.PADLOCK width={12} height={12} color={colors.text.dimmer} />
          <Text style={[styles.lockText, { color: colors.text.dimmer }]}>Replies disabled</Text>
        </View>
      )}

      {thread.kind === 'survey' && thread.survey && (
        <SurveyCardCTA survey={thread.survey} onPress={() => onPress(thread)} />
      )}
    </GlassCard>
  );
});

NewsfeedThreadCard.displayName = 'NewsfeedThreadCard';

const styles = StyleSheet.create({
  cardShadow: Platform.select({
    web: {},
    default: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 8,
      elevation: 4,
    },
  }) as any,
  announcementBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    marginBottom: 8,
  },
  announcementText: {
    fontSize: 11,
    fontWeight: '600',
    color: PRIMITIVE_COLORS.resioBlue,
  },
  authorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarImage: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  avatarText: {
    fontSize: 12,
    fontWeight: '600',
  },
  authorInfo: {
    flex: 1,
    gap: 1,
  },
  authorName: {
    ...REDESIGN_TYPOGRAPHY.authorName,
  },
  timestamp: {
    ...REDESIGN_TYPOGRAPHY.timestamp,
  },
  menuAnchor: {
    position: 'relative',
  },
  moreButton: {
    width: 28,
    height: 28,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.postTitle,
  },
  bodyContainer: {
    overflow: 'hidden',
    marginTop: 6,
  },
  body: {
    ...REDESIGN_TYPOGRAPHY.postBody,
  },
  previewImage: {
    width: '100%',
    height: 160,
    borderRadius: 10,
    marginTop: 8,
  },
  tagBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 5,
    marginTop: 12,
  },
  tagBadgeText: {
    fontSize: 11,
    fontWeight: '500',
  },
  footer: {
    borderTopWidth: 1,
    marginTop: 14,
    paddingTop: 12,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  stat: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  statText: {
    ...REDESIGN_TYPOGRAPHY.statText,
  },
  statTextActive: {
    color: PRIMITIVE_COLORS.resioBlue,
  },
  attachmentStat: {
    marginLeft: 'auto',
  },
  lockIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 8,
  },
  lockText: {
    fontSize: 11,
    fontStyle: 'italic',
  },
});
